(() => {
  const nonceKey = 'flexora-crm-sync-nonce';
  const pendingKey = 'flexora-crm-sync-pending';
  const pendingTtl = 30 * 60 * 1000;
  const gatewayUrl = document.body.dataset.crmGatewayUrl?.replace(/\/$/, '') || '';
  const pageHandle = document.body.dataset.pageHandle || '';
  let nonceRequest;

  if (!gatewayUrl || !/^https:\/\//.test(gatewayUrl)) return;

  const storageGet = (key) => {
    try {
      const value = window.sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (_error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
      // CRM sync is additive; Shopify's native submission remains authoritative on this page.
    }
  };

  const storageRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Storage is optional.
    }
  };

  const freshNonce = () => {
    const stored = storageGet(nonceKey);
    if (!stored?.nonce || Date.parse(stored.expiresAt) <= Date.now() + 15000) {
      storageRemove(nonceKey);
      return null;
    }
    return stored;
  };

  const requestNonce = async () => {
    const existing = freshNonce();
    if (existing) return existing;
    if (nonceRequest) return nonceRequest;
    nonceRequest = fetch(`${gatewayUrl}/api/intake/preflight`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('CRM preflight failed');
        const issued = await response.json();
        storageSet(nonceKey, issued);
        return issued;
      })
      .finally(() => {
        nonceRequest = undefined;
      });
    return nonceRequest;
  };

  const fieldValue = (form, name) => form.elements.namedItem(name)?.value?.trim() || '';
  const captureApplication = (form) => {
    const submittedAt = fieldValue(form, 'contact[submitted_at]') || new Date().toISOString();
    return {
      fullName: fieldValue(form, 'contact[name]'),
      adultConfirmed: Number(fieldValue(form, 'contact[age]')) >= 18,
      phone: fieldValue(form, 'contact[phone]'),
      email: fieldValue(form, 'contact[email]'),
      pageLanguage: fieldValue(form, 'contact[page_language]'),
      sourcePage: window.location.pathname,
      submittedAt,
      consentVersion: fieldValue(form, 'contact[consent_version]'),
      consentedAt: submittedAt,
      utm: {
        source: fieldValue(form, 'contact[utm_source]'),
        medium: fieldValue(form, 'contact[utm_medium]'),
        campaign: fieldValue(form, 'contact[utm_campaign]'),
        content: fieldValue(form, 'contact[utm_content]'),
        term: fieldValue(form, 'contact[utm_term]'),
      },
    };
  };

  const capturePending = (form) => {
    storageSet(pendingKey, {
      capturedAt: Date.now(),
      application: captureApplication(form),
    });
  };

  const getPending = () => {
    const pending = storageGet(pendingKey);
    if (!pending?.application || !Number.isFinite(pending.capturedAt) || Date.now() - pending.capturedAt > pendingTtl) {
      storageRemove(pendingKey);
      return null;
    }
    return pending;
  };

  const commitPending = async () => {
    const pending = getPending();
    if (!pending) return;
    try {
      const { nonce } = await requestNonce();
      const response = await fetch(`${gatewayUrl}/api/intake/commit`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ nonce, application: pending.application }),
      });
      if (!response.ok) throw new Error('CRM commit failed');
      storageRemove(pendingKey);
      storageRemove(nonceKey);
    } catch (_error) {
      // Keep the short-lived session queue. A refresh of the success page retries safely.
      storageRemove(nonceKey);
    }
  };

  document.querySelectorAll('[data-crm-sync-payload]').forEach((form) => {
    requestNonce().catch(() => {});
    form.addEventListener('focusin', () => requestNonce().catch(() => {}), { once: true });
    form.addEventListener('submit', (event) => {
      if (event.defaultPrevented) return;
      capturePending(form);
    });
  });

  if (pageHandle === 'thank-you') {
    commitPending();
  } else {
    getPending();
  }
})();
